import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Journey.module.scss';

type MilestoneId = 'plsql' | 'react' | 'uxui' | 'frontendLead' | 'fullstack';

interface Milestone {
  id: MilestoneId;
  year: string;
  tags: readonly string[];
  current?: true;
}

const MILESTONES: Milestone[] = [
  { id: 'plsql', year: '2020', tags: ['Oracle', 'PL/SQL', 'SQL'] },
  { id: 'react', year: '2021', tags: ['React', 'TypeScript', 'JavaScript', 'REST'] },
  { id: 'uxui', year: '2023', tags: ['Figma', 'Design Systems', 'Prototyping', 'User Research'] },
  { id: 'frontendLead', year: '2023', tags: ['Team Lead', 'Architecture', 'Code Review', 'Mentoring'] },
  { id: 'fullstack', year: '2025', tags: ['Node.js', 'Express', 'PostgreSQL', 'React', 'TypeScript'], current: true }
];

const ITEM_THRESHOLD = 0.2;

const Journey = () => {
  const { t } = useTranslation();
  const listRef = useRef<HTMLOListElement>(null);
  const [activeId, setActiveId] = useState<MilestoneId | null>(null);
  const [visibleIds, setVisibleIds] = useState<Set<MilestoneId>>(new Set());

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = (entry.target as HTMLElement).dataset.id as MilestoneId;
          if (entry.isIntersecting) {
            setVisibleIds((prev) => new Set([...prev, id]));
          } else {
            setVisibleIds((prev) => {
              const next = new Set(prev);
              next.delete(id);
              return next;
            });
          }
        });
      },
      { threshold: ITEM_THRESHOLD, rootMargin: '0px 0px -40px 0px' }
    );

    Array.from(list.children).forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  const toggle = (id: MilestoneId) => setActiveId((prev) => (prev === id ? null : id));

  return (
    <div className={styles.journey}>
      <ol className={styles.timeline} ref={listRef}>
        {MILESTONES.map(({ id, year, tags, current }) => (
          <li
            key={id}
            data-id={id}
            className={[
              styles.item,
              visibleIds.has(id) && styles.visible,
              activeId === id && styles.active,
              current && styles.current
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <button type='button' className={styles.trigger} onClick={() => toggle(id)} aria-expanded={activeId === id}>
              <div className={styles.rail}>
                <span className={styles.dot} aria-hidden='true' />
              </div>
              <div className={styles.content}>
                <span className={styles.year}>{year}</span>
                <h3 className={styles.title}>{t(`journey.milestone.${id}.title`)}</h3>
                <p className={styles.body}>{t(`journey.milestone.${id}.body`)}</p>
                <div className={styles.tagsWrapper}>
                  <div className={styles.tagsInner}>
                    <ul className={styles.tags}>
                      {tags.map((tag) => (
                        <li key={tag} className={styles.tag}>
                          {tag}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
};

export default Journey;
